import { useForm } from "react-hook-form";
import { ChangeEvent, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { zodResolver } from "@hookform/resolvers/zod";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { AtributosSistemaForm, AtributosSistemaFormDefaultValues, AtributosSistemaFormSchema } from "../schemas/atributos_sistema_form";
import { AtributoSistemaModel } from "../../domain/models/atributos_sistema";
import { EspacoUtilizadoBD } from "../../domain/models/espaco_utilizado_bd";
import { normalizeExtensionFile, uploadFileToBase64String } from "../../../../common/utils/utils";
import { downloadBase64File } from "../../../../common/utils/download_utils";

/**
 * useAtributosSistemaViewModel
 */

export const useAtributosSistemaViewModel = () => {
  const {
    administracao: {
      getAtributosSistemaUseCase,
      getSpaceUsedBdUseCase,
      updateAtributosSistemaUseCase,

    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<AtributosSistemaForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: AtributosSistemaFormDefaultValues,
    resolver: zodResolver(AtributosSistemaFormSchema),
  });


  const navigate = useNavigate();
  const location = useLocation();

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  //ARQUIVOS
  const [openDeleteArquivoModalOpen, setOpenDeleteArquivoModal] = useState(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [atributosSistema, setAtributosSistema] = useState<AtributoSistemaModel>();
  const [espacoUsadoBd, setEspacoUsadoBd] = useState<EspacoUtilizadoBD>();




  //#region CARREGAMENTO

  const loadAtributosSistema = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);
      const atributosSistema = await getAtributosSistemaUseCase.execute();
      hookForm.setValue('idAtributoSistema', atributosSistema.idAtributoSistema);
      hookForm.setValue('nomeCliente', atributosSistema.nomeCliente ?? '');
      //hookForm.setValue('logoCliente', atributosSistema.logoCliente != null ? (atributosSistema.logoCliente) : '');

      setAtributosSistema(atributosSistema);

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar atributos do sistema!'), { isAutoFinish: true });
    } finally {
      setLoading(false);
    }
  }, [
    getAtributosSistemaUseCase,
    pushErrorNotification
  ]);

  const loadEspacoUtilizadoBd = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage(undefined);

      const espacoUtilizadoBd = await getSpaceUsedBdUseCase.execute();
      setEspacoUsadoBd(espacoUtilizadoBd);

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar espaço utilizado no banco de dados!'), { isAutoFinish: true });
    } finally {
      setLoading(false);
    }
  }, [
    getSpaceUsedBdUseCase,
    pushErrorNotification

  ]);

  //#endregion CARREGAMENTO





  //#region CABEÇALHO

  const handlePageBack = useCallback(() => {
    navigate('/GruposAcesso_lista');
  }, [navigate]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadAtributosSistema();
      await loadEspacoUtilizadoBd();
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar espaço utilizado no banco de dados!'), { isAutoFinish: true });
    } finally {
      setLoading(false);
    }
  }, [loadAtributosSistema, loadEspacoUtilizadoBd, pushErrorNotification]);

  //#endregion



  //#region UPDATE


  const handleUpdateAtributosSistema = useCallback(async (form: AtributosSistemaForm) => {
    try {
      setSaving(true);
      if (form.idAtributoSistema !== undefined) {

        const normalizedArquivoLogo = form.logoCliente ? await uploadFileToBase64String(form.logoCliente) : undefined;
        const extensaoLogo = normalizeExtensionFile(form.logoCliente);

        await updateAtributosSistemaUseCase.execute(
          0,
          form.limparArquivo ?? false,
          form.nomeCliente ?? undefined,
          normalizedArquivoLogo,
          extensaoLogo
        );

        await loadAtributosSistema();
        await loadEspacoUtilizadoBd();
        pushInfoNotification("Sucesso", "Atributos do sistema alterados com sucesso!", { isAutoFinish: true });

      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao salvar atributos do sistema!"), { isAutoFinish: true });
    } finally {
      setSaving(false);
    }
  }, [
    updateAtributosSistemaUseCase,
    loadAtributosSistema,
    loadEspacoUtilizadoBd,
    pushInfoNotification,
    pushErrorNotification,
  ]);


  //#endregion





  //#region PAGE START
  usePageStartBootstrap(async () => {
    try {
      setPageLoading(true);
      await loadAtributosSistema();
      await loadEspacoUtilizadoBd();

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar atributos do sistema!")[0]);
    } finally {
      setPageLoading(false);
    }
  });

  //#endregion




  //#region IMAGEM LOGO

  const handleDownload = useCallback(async (arquivoNome: string, arquivo: string) => {
    if (!saving) {
      try {
        await downloadBase64File(arquivoNome, arquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
      }
    };
  }, [
    saving,
    pushErrorNotification,
  ]);

  const handleArquivoDeleteOpenState = useCallback((limparArquivos: string | null, arquivo: string | null, openState: boolean) => {
    if (openState && limparArquivos && arquivo) {
      setOpenDeleteArquivoModal(openState)
      setLimparArquivo(limparArquivos)
      setArquivo(arquivo)
    } else {
      setOpenDeleteArquivoModal(openState)
      setLimparArquivo(undefined)
      setArquivo(undefined)
    }

  }, [atributosSistema]);


  const handleConfirmarDeletarArquivo = useCallback(async () => {
    if (limparArquivo && arquivo) {
      hookForm.setValue(limparArquivo as any, true);
      hookForm.setValue(arquivo as any, null);
      setAtributosSistema(prev => prev ? ({ ...prev, [arquivo]: null }) : undefined)

      setOpenDeleteArquivoModal(false)
      setArquivo(undefined)
      setLimparArquivo(undefined)
    }
  }, [
    limparArquivo,
    arquivo,
    hookForm,
    atributosSistema
  ]);


  const handleFileChange = useCallback(async (nomeArquivo: string, limparArquivo: string, e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving) {
      const file = e.target.files?.item(0);
      if (file) {
        hookForm.setValue(limparArquivo as any, false);
        hookForm.setValue(nomeArquivo as any, file);
        onChange(file);

        // Converter para base64 e atualizar o estado para exibição em tempo real
        try {
          const base64String = await uploadFileToBase64String(file);
          setAtributosSistema(prev => prev ? ({
            ...prev,
            logoCliente: base64String,
            nomeArquivo: file.name
          }) : undefined);
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, 'Falha ao carregar a imagem!'));
        }
      }
    }
  }, [
    saving,
    hookForm,
    pushErrorNotification,
  ]);

  //#endregion





  //#region HISTORICO

  const handleOpenChangeHistory = useCallback((
    idTabela: number | null,
    tipoTabela: LogTipoTabela | null,
  ) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (true) {
        changeHistory.handleOpenChangeHistory(atributosSistema?.idAtributoSistema ?? 0, LogTipoTabela.ATRIBUTOS_SISTEMA, true);
      }
    }
  }, [
    atributosSistema,
    changeHistory,
  ]);

  //#endregion


  return ({
    loading,
    hookForm,
    saving,
    changeHistory,
    errorMessage,
    atributosSistema,
    espacoUsadoBd,
    openDeleteArquivoModalOpen,


    handleDownload,
    handleArquivoDeleteOpenState,
    handleConfirmarDeletarArquivo,
    handleFileChange,
    handleUpdateAtributosSistema: hookForm.handleSubmit(handleUpdateAtributosSistema),
    handlePageBack,
    handleReload,
    handleOpenChangeHistory,
  });
}